import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { authApi, type Me } from "../rpc";

type State = {
  me: Me | null;
  loading: boolean;
};

type ContextValue = {
  state: State;
  refetch: () => Promise<void>;
  logout: () => Promise<void>;
};

const MeContext = createContext<ContextValue | null>(null);

export const MeProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<State>({ me: null, loading: true });

  const refetch = useCallback(async () => {
    try {
      const res = await authApi.me.$get();
      if (res.status === 200) {
        const data = await res.json();
        setState({ me: data.user, loading: false });
      } else {
        setState({ me: null, loading: false });
      }
    } catch {
      setState({ me: null, loading: false });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout.$post();
    } finally {
      setState({ me: null, loading: false });
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return <MeContext.Provider value={{ state, refetch, logout }}>{children}</MeContext.Provider>;
};

export const useMe = (): ContextValue => {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe must be used within MeProvider");
  return ctx;
};
